import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { classNames } from "primereact/utils";
import localeText from "../../../../locale";
import IntegrationService from "../../../../service/IntegrationService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import { getToken, redirectIfUnauthorized } from "../../utils/adminUtils";
import FormField from "../../components/form/FormField";
import "./AdminIntegrations.css";

// Carrega a configuração atual e salva; como no clássico, sucesso = requisição sem erro
const useIntegrationConfig = ({ load, save, fields }) => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const [values, setValues] = useState(() => Object.fromEntries(fields.map((f) => [f, ""])));
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let mounted = true;
    load()
      .then((current) => {
        if (!mounted) return;
        if (current) {
          setValues(current);
          setSaved(current);
        }
      })
      .catch((e) => {
        console.log(e);
        if (mounted) setLoadFailed(true);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (successSummary) => {
    const missing = fields.filter((field) => !values[field]?.trim());
    if (missing.length) {
      setErrors(Object.fromEntries(missing.map((field) => [field, localeText("adminFormRequired")])));
      return;
    }
    setSaving(true);
    try {
      const trimmed = Object.fromEntries(fields.map((field) => [field, values[field].trim()]));
      await save(trimmed);
      setValues(trimmed);
      setSaved(trimmed);
      showSuccessToast({ detail: successSummary });
    } catch (e) {
      if (redirectIfUnauthorized(e)) return;
      console.log(e);
      showErrorToast({ detail: "pixelConfigErrorDetail" });
    } finally {
      setSaving(false);
    }
  };

  const change = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const dirty = fields.some((field) => (values[field] || "") !== (saved?.[field] || ""));

  return { values, saved, loading, loadFailed, saving, errors, submit, change, dirty };
};

const IntegrationCard = ({ id, highlighted, icon, title, description, config, children, onSave }) => (
  <section id={id} className={classNames("admin-integration", { "admin-integration--highlight": highlighted })}>
    <header className="admin-integration__header">
      <span className="admin-integration__icon" aria-hidden="true">
        <i className={icon} />
      </span>
      <div className="admin-integration__title-group">
        <h2 className="admin-integration__title">{title}</h2>
        <p className="admin-muted">{description}</p>
      </div>
      {!config.loading && (
        <span className={`admin-badge admin-badge--${config.saved ? "good" : "neutral"}`}>
          <i className={config.saved ? "pi pi-check-circle" : "pi pi-minus-circle"} aria-hidden="true" />
          {localeText(config.saved ? "adminIntegrationsConfigured" : "adminIntegrationsNotConfigured")}
        </span>
      )}
    </header>

    {config.loadFailed && (
      <p className="admin-integration__warning">
        <i className="pi pi-exclamation-triangle" aria-hidden="true" />
        {localeText("adminIntegrationsLoadFailed")}
      </p>
    )}

    <form
      className="admin-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      {children}
      <div className="admin-integration__actions">
        <button
          type="submit"
          className="admin-btn admin-btn--primary"
          disabled={config.loading || config.saving || !config.dirty}
        >
          {config.saving && <i className="pi pi-spin pi-spinner" aria-hidden="true" />}
          {localeText("savePixelConfig")}
        </button>
      </div>
    </form>
  </section>
);

const AdminIntegrations = () => {
  const { pathname } = useLocation();
  const gtmRef = useRef(null);
  const focusGtm = pathname.startsWith("/dashboard-gtm");

  const meta = useIntegrationConfig({
    fields: ["accessToken", "pixelId"],
    load: async () => {
      const res = await new IntegrationService().getPixel(getToken());
      const result = res?.result;
      return result?.facebook_access_token && result?.facebook_pixel_id
        ? { accessToken: result.facebook_access_token, pixelId: result.facebook_pixel_id }
        : null;
    },
    save: ({ accessToken, pixelId }) =>
      new IntegrationService().configPixel(accessToken, pixelId, getToken()),
  });

  const gtm = useIntegrationConfig({
    fields: ["gtmCode"],
    load: async () => {
      const res = await new IntegrationService().getGtm(getToken());
      return res?.result?.gtmcode ? { gtmCode: res.result.gtmcode } : null;
    },
    save: ({ gtmCode }) => new IntegrationService().configGtm(gtmCode, getToken()),
  });

  // Aberto pela rota do GTM: leva direto ao cartão dele
  useEffect(() => {
    if (focusGtm) gtmRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusGtm]);

  return (
    <div className="admin-integrations">
      <IntegrationCard
        id="meta-ads"
        highlighted={!focusGtm && pathname.startsWith("/dashboard-metaads")}
        icon="pi pi-facebook"
        title="Meta Ads · Pixel"
        description={localeText("adminIntegrationsMetaDescription")}
        config={meta}
        onSave={() => meta.submit("pixelConfigSaved")}
      >
        <FormField
          label={localeText("accessToken")}
          htmlFor="meta-access-token"
          error={meta.errors.accessToken}
          required
        >
          <input
            id="meta-access-token"
            className="admin-input admin-input--mono"
            value={meta.values.accessToken}
            onChange={(e) => meta.change("accessToken", e.target.value)}
            disabled={meta.loading}
            autoComplete="off"
            spellCheck={false}
          />
        </FormField>
        <FormField
          label={localeText("pixelNumberId")}
          htmlFor="meta-pixel-id"
          error={meta.errors.pixelId}
          required
        >
          <input
            id="meta-pixel-id"
            className="admin-input admin-input--mono"
            value={meta.values.pixelId}
            onChange={(e) => meta.change("pixelId", e.target.value)}
            disabled={meta.loading}
            autoComplete="off"
            inputMode="numeric"
          />
        </FormField>
      </IntegrationCard>

      <div ref={gtmRef}>
        <IntegrationCard
          id="google-tag-manager"
          highlighted={focusGtm}
          icon="pi pi-google"
          title="Google Tag Manager"
          description={localeText("adminIntegrationsGtmDescription")}
          config={gtm}
          onSave={() => gtm.submit("gtmCodeSaved")}
        >
          <FormField
            label={localeText("gtmCode")}
            htmlFor="gtm-code"
            hint={localeText("adminIntegrationsGtmHint")}
            error={gtm.errors.gtmCode}
            required
          >
            <input
              id="gtm-code"
              className="admin-input admin-input--mono"
              value={gtm.values.gtmCode}
              onChange={(e) => gtm.change("gtmCode", e.target.value)}
              disabled={gtm.loading}
              placeholder="GTM-XXXXXXX"
              autoComplete="off"
              spellCheck={false}
            />
          </FormField>
        </IntegrationCard>
      </div>
    </div>
  );
};

export default AdminIntegrations;
